/**
 * The Item list — the console's one row view, rendered from `GET /api/items`.
 *
 * Structure, spacing and every piece of chrome come from the source design's
 * `ITEM LIST + VIEWS` block: header (crumb · count, title), the chip row with
 * the Group and Sort controls, then one card per group with the design's row
 * layout — kind badge, title, severity, location meta, age,
 * stalled marker.
 *
 * ## Ball-in-court is gone (spec §4)
 *
 * The design's row carries a ball pill, the chip row a `Ball` filter, and the
 * group cycle a `Ball-in-court` bucket. All three are cut console-wide:
 * `ballHolder` is a free-text denormalized assignee that reads `Unassigned`
 * on essentially every real row in this account, so the pill repeated one
 * word and the bucket was one group. The server still SUPPORTS
 * `groupBy: "ball"` — `listItems` owns that vocabulary and `getMyWork`
 * partitions on the field — but the console no longer offers it, and
 * `tests/guards/noBallAffordance.test.ts` keeps it that way.
 *
 * ## Failures render as failures
 *
 * A 401, 403, 400 or 503 renders its own panel naming what happened. This is
 * the entire reason Task 5c made the workspace explicit: a forged team
 * workspace collapsed into "no rows" is indistinguishable from an empty one,
 * so the console must never turn a status into an empty list.
 */

import { useEffect, useMemo, useState } from "react";
import { postItemEdit, type ApiError, type ConsoleGroupBy, type ConsoleRow, type ItemsResponse } from "../api";
import { ItemFlowBandChip, ItemFlowCards } from "../components/ItemFlowCards";
import { LIST_TYPE as TABLE_TYPE } from "../listTypography";
import { buildItemFlow, inAgeBand } from "../lib/itemFlow";
import type { ConsoleSort } from "../../server/projection/priority";
import {
  columnSortParams,
  compareMaybe,
  compareText,
  readColumnSort,
  sortByColumn,
  type ColumnSort,
  type SortDirection,
} from "../components/columnSort";
import {
  anyColumnFilter,
  applyColumnFilters,
  clearColumnFilterParams,
  columnFilterLabel,
  columnFilterOptions,
  readColumnFilters,
  NO_VALUE,
  type ColumnFilterDef,
  type ColumnFilterOption,
} from "../components/columnFilter";
import { ColumnHeader } from "../components/ColumnHeader";
import { isoDay, parseDay } from "../components/Calendar";
import { MenuControl } from "../components/MenuControl";
import { PriorityMark } from "./RecordDetail";
import { observationFacts } from "./records/recordModel";
import { RecordsWorkspace } from "./records/RecordsWorkspace";
// Observation facts moved to the records model; kept importable from here.
export { observationFacts, type ObservationFacts } from "./records/recordModel";
import { AssigneeAvatar } from "../../../../backend/src/frontend/components/shared/assignee/AssigneeAvatar";
import type { ConsoleSeverity } from "../../server/queries/listItems";
// The phone's own daily-log taxonomy: its labels, and the section glyphs its
// day-segmented list has always drawn. Imported rather than restated so the
// console's log view and the app's cannot disagree about either.
import {
  PROCORE_DAILY_LOG_CATEGORY_DEFINITIONS,
  getProcoreDailyLogCategoryGlyph,
} from "../../../../backend/src/shared/procoreDailyLog";
import { C, FONT_MONO, projectDot } from "../tokens";
import { ViewSkeleton } from "../components/Skeleton";
import { NudgeSheet } from "../components/NudgeSheet";
import { sharedSyncSource } from "../lib/integrationName";
// The console's shared chrome. These used to be defined here, privately; they
// moved to `components/chrome.tsx` when the Brief, My Work, Item detail and
// Daily Log needed the same kind chip, the same age column and the same
// failure panel. One spelling of "what does a stalled row look like".
import {
  Centered,
  EmptyPanel,
  ASK_COMPOSER_CLEARANCE,
  EvidenceTile,
  FailurePanel,
  KIND_ICON,
  KindChip,
  META,
  Pill,
  PagedListEnd,
  ROW_LINE,
  ROW_TITLE,
  SEVERITY,
  StaleBanner,
  MONTHS,
  dateWithYear,
  TABLE_COLUMN_WIDTH,
  ageOf,
  initialsOf,
  dayLabel,
  humanize,
  plural,
  usePagedList,
} from "../components/chrome";

// ---------------------------------------------------------------------------
// Saved views — the sidebar's kind selection (spec §3.1, "the 4 saved views")
// ---------------------------------------------------------------------------

export type SavedViewId = "all" | "log" | "obs" | "task" | "punch" | "issues";

export interface SavedView {
  id: SavedViewId;
  name: string;
  /** The `ConsoleItem.kind` this view shows, or `undefined` for All items. */
  kind?: ConsoleRow["kind"];
  hint: string;
  d1: string;
  d2: string;
}

/**
 * Which rows a saved view shows.
 *
 * Kind alone is not enough for the two log-shaped views. An OBSERVATION on a
 * Procore-format walk is stored as a daily log item whose category is
 * `observations` — `kind: "log"`, `recordType: "observation"` — because that
 * is the record Procore has. Matching the Observations view on
 * `kind === "observation"` therefore matched only the `Note` collection,
 * which a Procore-format project never writes: the view was empty by
 * construction, here and for any real customer (production holds 738
 * observation-category log items against 147 notes).
 *
 * So the two split on `recordType`, and Daily logs stops claiming the
 * observations that are not its own.
 */
export function matchesSavedView(view: SavedView, row: ConsoleRow): boolean {
  if (view.id === "issues") {
    return row.kind === "observation" || row.recordType === "observation" ||
      row.kind === "task" || row.kind === "punch";
  }
  if (view.id === "obs") return row.kind === "observation" || row.recordType === "observation";
  if (view.id === "log") return row.kind === "log" && row.recordType !== "observation";
  return !view.kind || row.kind === view.kind;
}

/** Hints verbatim from the design's `VIEW_HINTS`. */
export const SAVED_VIEWS: SavedView[] = [
  {
    id: "all",
    name: "All items",
    hint: "Everything in this workspace, whatever it is.",
    d1: "M4 6h16M4 12h16M4 18h10",
    d2: "",
  },
  {
    id: "log",
    name: "Daily logs",
    kind: "log",
    hint: "What happened on site each day. A record, not open work.",
    ...KIND_ICON.log,
  },
  {
    id: "obs",
    name: "Observations",
    kind: "observation",
    hint: "What was seen and worth noting. No one owes an action yet.",
    ...KIND_ICON.observation,
  },
  {
    id: "task",
    name: "Tasks",
    kind: "task",
    hint: "What someone owes you, and who is holding it up.",
    ...KIND_ICON.task,
  },
  {
    id: "punch",
    name: "Punch",
    kind: "punch",
    hint: "What still blocks closeout on a finished area.",
    ...KIND_ICON.punch,
  },
];

const ISSUES_VIEW: SavedView = {
  id: "issues",
  name: "Issues",
  hint: "Observations, tasks, and punch items for this Autodesk project.",
  ...KIND_ICON.observation,
};

/** The selected project's provider determines its record navigation. */
export function projectSavedViews(provider?: "procore" | "autodesk"): SavedView[] {
  if (provider !== "autodesk") return SAVED_VIEWS;
  return [
    SAVED_VIEWS[0]!,
    { ...SAVED_VIEWS[1]!, name: "Daily reports" },
    ISSUES_VIEW,
  ];
}

/** Keep existing bookmarks and project switches on a visible destination. */
export function projectSavedView(view: SavedViewId, provider?: "procore" | "autodesk"): SavedViewId {
  if (provider === "autodesk" && (view === "obs" || view === "task" || view === "punch")) {
    return "issues";
  }
  if (provider !== "autodesk" && view === "issues") return "obs";
  return view;
}

export function isSavedViewId(value: string | null): value is SavedViewId {
  return value === "issues" || (!!value && SAVED_VIEWS.some((view) => view.id === value));
}

// ---------------------------------------------------------------------------
// Grouping — live. The labels are the design's; the values are the server's.
// ---------------------------------------------------------------------------

/* The group CYCLE and its labels are gone with the pill that read them — the
   server still honours `?groupBy=`, so the capability is a URL away and the
   control is three lines to restore. Keeping a table nothing rendered would
   have left the next reader unable to tell whether Group was coming back. */

/** The group header's dot. Status buckets get the palette's workflow colours;
 *  a project bucket gets that project's derived tone, which is why the dot
 *  needs a row (groups carry only a label). */
/**
 * One item, as a row in a list. Spelled once because the list draws it under
 * two different arrangements now — the server's groups, and the log view's
 * days — and a row that differed between them would be two rows.
 */
/**
 * The column header over a list of item rows.
 *
 * The redesign names the columns once, in the mono voice, rather than
 * repeating a label on every row. It is `aria-hidden` and not a real
 * `<table>`: the rows below are links, each one already carrying its own
 * accessible name, and a table wrapper would announce a grid the keyboard
 * cannot navigate as one.
 */
/**
 * The column head over the sync state.
 *
 * "Procore" rather than a generic word when every row is on a Procore
 * project: the column says whether the record reached the system of record,
 * and naming it is the point. Its cells read `procoreSync` only, so anywhere
 * else — an Autodesk project, a mixed or unlinked list — it is "Sync", never
 * "Procore" (LED-763: an Autodesk customer must not read Procore copy).
 */
function syncColumnName(rows: ReadonlyArray<Pick<ConsoleRow, "syncSource">>): string {
  return sharedSyncSource(rows) === "procore" ? "Procore" : "Sync";
}

/** Statuses that mean the work is DONE — the console's own two terminal
 *  values, which `TERMINAL_STATUSES` on the server projects three raw task
 *  statuses into. A row in one of these arrives with its box already ticked. */
const TERMINAL_ROW_STATUSES: ReadonlySet<ConsoleRow["status"]> = new Set([
  "closed",
  "logged",
]);

/**
 * What unticking a box writes back.
 *
 * The status the row ARRIVED with, in the field's own vocabulary — so a task
 * that was in progress goes back to in progress rather than being reopened
 * from scratch. A row that was already terminal when the page loaded has no
 * before-state to restore, and `in_progress` is the honest answer there:
 * somebody is reopening finished work, which is work in progress.
 */
function reopenStatusFor(status: ConsoleRow["status"]): string {
  if (status === "open") return "initiated";
  if (status === "ready_for_review") return "ready_for_review";
  return "in_progress";
}

/** The row's single trailing action, in the design's quiet text style. */
function ActionButton({
  testId,
  busy,
  tone,
  onClick,
  children,
}: {
  testId: string;
  busy?: boolean;
  tone: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      disabled={busy}
      className="kb"
      style={{
        background: "transparent",
        border: "none",
        cursor: busy ? "progress" : "pointer",
        fontFamily: "inherit",
        fontSize: 14.37,
        fontWeight: 600,
        color: tone,
        textAlign: "right",
        whiteSpace: "nowrap",
        padding: 0,
      }}
    >
      {children}
    </button>
  );
}

/**
 * Which records the tick box belongs to.
 *
 * Only TASKS. The box is not decoration — it posts
 * `status: "completed"`, which is a value from `ACTION_STATUS_VALUES`, the
 * action model's own enum. An observation and a punch item are not actions:
 * a punch item's completion lives in `workflowStatus`, and an observation
 * runs Initiated → Ready → Closed. Offering the same box on all three wrote
 * a word those records have never heard, and put a task's affordance on two
 * kinds that are read rather than ticked (user-directed, 2026-09-09).
 *
 * Daily logs never had it, for the plainer version of the same reason: a log
 * entry is a record of what happened, not open work.
 */
function canComplete(row: ConsoleRow): boolean {
  return row.kind === "task";
}

/**
 * The design's row, shared by the header and every row under it.
 *
 * Declared once because a table whose header and body each spell their own
 * columns is a table whose columns drift. The shape is the artboard's:
 * a checkbox, the age, the item and what it says about itself, where it
 * stands with the integration, and the one action.
 */
const ITEM_GRID: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "28px 40px minmax(0, 1fr) minmax(0, auto) 44px",
  alignItems: "center",
  gap: 14,
  // 12 on the left rather than 18: the checkbox's hit area overhangs its
  // column by 8px on each side so it is a 44px target, and the design pulls
  // the row's own padding in to meet it.
  padding: "0 18px 0 12px",
};

/** `ITEM_GRID` with the age track widened to hold a created date
 *  (`Sep 15, 2026`) — the Tasks list's column (LED-772). */
const ITEM_GRID_CREATED: React.CSSProperties = {
  ...ITEM_GRID,
  gridTemplateColumns: "28px 88px minmax(0, 1fr) minmax(0, auto) 44px",
};

function ItemColumns({
  integration,
  sort,
  onSort,
  created = false,
}: {
  integration: string;
  sort: ColumnSort | null;
  onSort: (column: string, direction: SortDirection) => void;
  /** Show the Created date in the age column's place (LED-772). */
  created?: boolean;
}) {
  return (
    <div
      // No longer `aria-hidden`: two of these cells are buttons now, and a
      // hidden subtree is one a keyboard cannot reach.
      style={{
        ...(created ? ITEM_GRID_CREATED : ITEM_GRID),
        height: 36,
        ...META,
        fontSize: 10.35,
        letterSpacing: "0.09em",
        color: C.sub2,
      }}
    >
      <span />
      <ColumnHeader
          column={created ? "created" : "age"}
          label={created ? "Created" : "Age"}
          sort={sort}
          onSort={onSort}
        />
      <ColumnHeader
          column="title"
          label="Item"
          sort={sort}
          onSort={onSort}
        />
      <span style={{ textAlign: "right" }}>{integration}</span>
      <span />
    </div>
  );
}

/** What the integration has done with this record, in one word and a dot.
 *  `undefined` for a record that has never been offered to one — a blank
 *  cell, not "Nothing to sync", which is a claim about a sync that ran. */
function syncState(row: ConsoleRow): { label: string; dot: string } | undefined {
  // An Autodesk record's Procore state is a stray stamp, not a sync.
  if (row.syncSource === "autodesk") return undefined;
  const status = row.procoreSync?.status;
  if (!status || status === "idle") return undefined;
  if (status === "synced") return { label: "Synced", dot: C.green };
  if (status === "failed") return { label: "Sync failed", dot: C.red };
  if (status === "syncing" || status === "pending") return { label: "Syncing", dot: C.amber };
  return { label: humanize(status), dot: C.sub2 };
}

/**
 * One record, as the design's Issues row draws it.
 *
 * ## The checkbox is a real write
 *
 * It posts the same item edit the phone's own editor posts — through
 * `/api/items/:kind/:itemId/edit`, which the console forwards verbatim to
 * `backend`'s `updateReviewedItem`. There is no console-side idea of "done":
 * the status it writes is the field's own vocabulary (`completed`), and the
 * backend decides what that means for the record and its integration, exactly
 * as it does when somebody ticks the same box on site.
 *
 * It is OPTIMISTIC, and it says so when it fails. A checkbox that waits for a
 * round trip before moving is a checkbox people click twice; one that moves
 * and then silently rolls back is worse. It moves, and a failure puts it back
 * with the reason attached to the row that failed.
 *
 * ## Undo writes the status the row arrived with
 *
 * Not a fixed "open". The list holds the record's status from before the
 * click, so unticking restores THAT — a task that was in progress goes back
 * to in progress rather than being quietly reopened from scratch.
 */
function ItemRow({
  row,
  first,
  href,
  onOpen,
  done,
  busy,
  failure,
  onToggle,
  onNudge,
  siteName,
  created = false,
}: {
  row: ConsoleRow;
  first: boolean;
  href: string;
  onOpen: () => void;
  /** Terminal in the data, or ticked in this session and not yet refetched. */
  done?: boolean;
  busy?: boolean;
  /** Why the last write on this row did not land. */
  failure?: string;
  /**
   * Absent where "done" is not a thing this record does — see `canComplete`.
   * The checkbox column stays, empty, so every list still lines its columns
   * up whether or not its rows can be ticked.
   */
  onToggle?: () => void;
  onNudge?: () => void;
  /**
   * The project, printed after the owner when the list spans more than one.
   *
   * The artboard's subline is priority · owner and stops there — its rows are
   * one project's worth of invented work, so it never has to answer "which
   * job is this on". This list opens on All projects by default, and the
   * column it replaced was exactly that answer. It is dropped when the list
   * is already filtered to one project, where it would repeat the header on
   * every row.
   */
  siteName?: string;
  /** Show the Created date in the age column's place (LED-772). */
  created?: boolean;
}) {
  const age = ageOf(row);
  const severity = row.severity ? SEVERITY[row.severity] : undefined;
  const sync = syncState(row);
  // The RESOLVED assignee, never the raw holder string — see
  // `tests/guards/noBallAffordance.test.ts` for why `ballHolder` may not
  // reach a view at all.
  const owner = row.assigneeName || "No owner";
  const highlightRow = row.kind !== "log" || row.recordType === "observation";

  return (
    <div
      className={highlightRow ? "record-row" : undefined}
      data-completed={done || undefined}
      style={{
        ...(created ? ITEM_GRID_CREATED : ITEM_GRID),
        minHeight: 50,
        borderTop: first ? undefined : `1px solid ${C.hairRow}`,
        // A done row recedes onto the inset rather than leaving the list —
        // the reader has to be able to see what they just ticked.
        background: highlightRow ? undefined : done ? C.inset : "transparent",
      }}
    >
      {/* OUTSIDE the link. A checkbox nested in an anchor is a control that
          navigates when the pointer misses it by a pixel. */}
      {onToggle ? (
      <button
        data-testid="item-done"
        data-item-id={row.id}
        aria-label={done ? `Reopen ${row.title}` : `Mark ${row.title} done`}
        aria-pressed={done}
        disabled={busy}
        onClick={onToggle}
        className="kb"
        style={{
          // 44 × 50 of target inside a 28px column: the design pulls it out
          // by 8px on each side so the box is comfortable to hit without
          // widening the column it sits in.
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 44,
          height: 50,
          margin: "0 -8px",
          background: "transparent",
          border: "none",
          cursor: busy ? "progress" : "pointer",
        }}
      >
        <span
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 22,
            height: 22,
            borderRadius: 7,
            border: `1.5px solid ${done ? C.green : C.line2}`,
            background: done ? C.green : C.surface,
            boxShadow: done
              ? `0 1px 2px ${C.softShadow}`
              : `inset 0 1px 1px ${C.softShadowLow}`,
            opacity: busy ? 0.5 : 1,
          }}
        >
          {done && (
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
              <path
                d="M20 6 9 17l-5-5"
                stroke={C.surface}
                strokeWidth="2.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </span>
      </button>
      ) : (
        <span />
      )}

      {created ? (
        <span
          data-testid="created-date"
          style={{ fontFamily: FONT_MONO, fontSize: TABLE_TYPE.mono, color: done ? C.sub2 : C.sub, whiteSpace: "nowrap" }}
        >
          {dateWithYear(row.createdAt)}
        </span>
      ) : (
        <span style={{ fontFamily: FONT_MONO, fontSize: TABLE_TYPE.mono, color: done ? C.sub2 : age.ink }}>
          {age.text}
          {row.stalled && !done && (
            <span
              data-testid="stalled-marker"
              style={{
                display: "inline-block",
                width: 5,
                height: 5,
                borderRadius: 3,
                background: age.ink,
                marginLeft: 4,
                verticalAlign: "middle",
              }}
            />
          )}
        </span>
      )}

      <a
        className={highlightRow ? "kb" : "kb frow"}
        data-testid="item-row"
        data-item-id={row.id}
        href={href}
        onClick={(event) => {
          event.preventDefault();
          onOpen();
        }}
        style={{
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          gap: 3,
          padding: "10px 0",
          color: "inherit",
        }}
      >
        <span
          data-testid="row-title"
          title={row.title}
          style={{
            fontSize: TABLE_TYPE.title,
            fontWeight: TABLE_TYPE.titleWeight,
            lineHeight: 1.35,
            color: done ? C.sub : C.ink,
            textDecoration: done ? "line-through" : "none",
            // TWO lines, then an ellipsis. The old row was one line and every
            // record longer than the column simply lost its ending, which on
            // a punch list is where the instruction usually is.
            textWrap: "pretty",
            overflow: "hidden",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
          }}
        >
          {row.title}
        </span>
        <span
          style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0, flexWrap: "wrap" }}
        >
          {failure ? (
            <span data-testid="item-done-failed" style={{ fontSize: 13.8, color: C.red }}>
              {failure}
            </span>
          ) : done ? (
            <span style={{ fontSize: 13.8, color: C.sub2 }}>Done</span>
          ) : (
            <>
              {severity && (
                <span
                  style={{
                    fontSize: TABLE_TYPE.priority,
                    fontWeight: 500,
                    color: severity.ink,
                    whiteSpace: "nowrap",
                  }}
                >
                  {severity.label}
                </span>
              )}
              {severity && (
                <span
                  style={{ width: 3, height: 3, borderRadius: 2, background: C.line2 }}
                />
              )}
              <span
                style={{
                  fontSize: TABLE_TYPE.name,
                  color: C.sub,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {owner}
              </span>
              {siteName && (
                <>
                  <span
                    style={{ width: 3, height: 3, borderRadius: 2, background: C.line2 }}
                  />
                  <span
                    style={{
                      fontSize: TABLE_TYPE.name,
                      color: C.sub2,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {siteName}
                  </span>
                </>
              )}
            </>
          )}
        </span>
      </a>

      <span
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 5,
          ...META,
          fontSize: 10.35,
          letterSpacing: "0.08em",
          color: C.sub,
          whiteSpace: "nowrap",
        }}
      >
        {sync && (
          <>
            <span
              style={{ width: 6, height: 6, borderRadius: 3, background: sync.dot }}
            />
            {sync.label}
          </>
        )}
      </span>

      {/* Undo is the checkbox's own action, so a row without a checkbox never
          offers it — it used to, and on a closed observation it was a button
          reading "Undo" wired to `undefined`. A closed record with no way to
          reopen it here gets no action at all rather than "Nudge", which asks
          somebody about work that is finished. */}
      {done ? (
        onToggle ? (
          <ActionButton testId="item-action" busy={busy} tone={C.sub} onClick={onToggle}>
            Undo
          </ActionButton>
        ) : (
          <span />
        )
      ) : onNudge ? (
        <ActionButton testId="item-action" busy={busy} tone={C.amber} onClick={onNudge}>
          Nudge
        </ActionButton>
      ) : (
        <span />
      )}
    </div>
  );
}

/**
 * How wide the page's own column is allowed to get.
 *
 * The list used to roll its own scroller with no maximum, so on a 2000px pane
 * a ten-column table stretched to 2000px and every column inherited the
 * slack. The artboards draw this table 1280px wide; 1180 is that less the
 * page's own 28px gutters, and it is the width the columns below are sized
 * against. Wider than the content needs is not neutral — it is what put the
 * gaps in.
 *
 * The number itself lives in `chrome.tsx` as `TABLE_COLUMN_WIDTH`, because
 * `ViewHeader`/`ViewBody`'s `wide="table"` — which is how Walks reaches the
 * same measure — already had to know it. Two files holding the same 1180
 * under two names is one edit away from two different table widths.
 */
const TABLE_MAX_WIDTH = TABLE_COLUMN_WIDTH;

/**
 * The views that carry the flow charts, and what one row is called on each
 * (user, 2026-09-11: "add these charts into the top of observations and tasks
 * and punch").
 *
 * The three lists that are open WORK. `All items` is deliberately out: its
 * rows are four kinds with four different lifecycles, so one "typical close"
 * over the mixture would be a number about nothing. Daily logs are out for
 * the reason `buildItemFlow` drops them anyway — a log is a record, not work.
 *
 * The design gates these cards on `tab !== 'task'`, i.e. it hides them on
 * tasks. Tasks are the kind with a real due date and an assignee, so how long
 * they sit is if anything the most useful place for it; the ask overrides the
 * artboard here.
 */
export const FLOW_NOUN: Record<string, string> = {
  issues: "issue",
  obs: "observation",
  task: "task",
  punch: "punch item",
};

/** Three bars' rank, for sorting by priority. */
const PRIORITY_RANK: Record<string, number> = { low: 1, medium: 2, high: 3, urgent: 4 };

/** When the record was captured, as a number to sort by; `undefined` for a
 *  hand-built row without one, which `compareMaybe` sorts last. */
function createdTime(row: ConsoleRow): number | undefined {
  const time = Date.parse(row.createdAt ?? "");
  return Number.isFinite(time) ? time : undefined;
}

/**
 * The order the record tables open in when the URL names no column: newest
 * capture first (LED-772). Supers and PMs want what was just walked at the
 * top, and the Created column is what shows it.
 */
const NEWEST_FIRST: ColumnSort = { column: "created", direction: "desc" };

/** The views that show a Created column and open newest-first. */
const CREATED_VIEWS: ReadonlySet<SavedViewId> = new Set(["obs", "issues", "task", "punch"]);

function itemColumnCompare(
  direction: SortDirection,
  siteNameOf: (jobSiteId: string) => string,
): Record<string, (a: ConsoleRow, b: ConsoleRow) => number> {
  const facts = (row: ConsoleRow) => observationFacts(row);
  return {
    number: (a, b) =>
      compareMaybe(facts(a).number, facts(b).number, direction, compareText),
    title: (a, b) => compareText(a.title, b.title),
    priority: (a, b) =>
      compareMaybe(
        facts(a).severity && PRIORITY_RANK[facts(a).severity!],
        facts(b).severity && PRIORITY_RANK[facts(b).severity!],
        direction,
        (left, right) => left - right,
      ),
    // Age is `daysSinceMovement`, so ascending is NEWEST first and the
    // column's preferred `desc` puts the oldest on top.
    age: (a, b) => a.daysSinceMovement - b.daysSinceMovement,
    created: (a, b) =>
      compareMaybe(createdTime(a), createdTime(b), direction, (left, right) => left - right),
    assignee: (a, b) =>
      compareMaybe(facts(a).assignee, facts(b).assignee, direction, compareText),
    company: (a, b) =>
      compareMaybe(
        a.assigneeCompanySource === "assignee-name" ? undefined : a.assigneeCompany,
        b.assigneeCompanySource === "assignee-name" ? undefined : b.assigneeCompany,
        direction,
        compareText,
      ),
    due: (a, b) =>
      compareMaybe(
        facts(a).due?.getTime(),
        facts(b).due?.getTime(),
        direction,
        (left, right) => left - right,
      ),
    location: (a, b) =>
      compareMaybe(facts(a).location, facts(b).location, direction, compareText),
    project: (a, b) => compareText(siteNameOf(a.jobSiteId), siteNameOf(b.jobSiteId)),
    photos: (a, b) => (a.photoCount ?? 0) - (b.photoCount ?? 0),
  };
}

/** The direction each column takes on its FIRST tap — see above. */
/**
 * The columns that carry a filter menu, and what each one reads a row for
 * (design: "Led AI Console", `FDEFS`).
 *
 * Age is deliberately absent: it is ordered, not narrowed, and the flow
 * chart's band bar above the table already filters by it with a picture
 * attached. Two age filters on one screen would be two answers to one
 * question.
 *
 * Status is absent for a different reason — both tables GROUP by it, so the
 * whole vocabulary is already on screen as headings, and a filter would be a
 * second control for what scrolling already does.
 */
/**
 * The "no filter" row's value inside a `MenuControl`.
 *
 * `ColumnFilterOption` says "all" with `value: null`, which a menu whose
 * values are strings cannot carry. Not `""` — an empty string is what an
 * unset parameter already looks like, and the two would be indistinguishable
 * in a `data-value` attribute a test reads.
 */
const ALL_OPTION = "__all__";

export const ITEM_COLUMN_FILTERS: ColumnFilterDef[] = [
  /* No PRIORITY here, and it is not an oversight.
  
     Priority already has a filter — the row's own card, which writes
     `?severity=` and is applied by `listItems` on the SERVER. A second one
     here would have been a client-side filter on the same dimension, and the
     two could disagree: card on High, column on Medium, and the table empties
     with nothing on screen explaining which of the two controls did it. The
     Priority column still sorts; what it does not do is offer a rival answer
     to a question the row above it already asks. */
  {
    key: "assignee",
    allLabel: "Anyone",
    noneLabel: "Unassigned",
    valueOf: (row) => observationFacts(row).assignee ?? null,
  },
  {
    key: "company",
    allLabel: "All companies",
    noneLabel: "No company",
    valueOf: (row) => row.assigneeCompanySource === "assignee-name" ? null : row.assigneeCompany ?? null,
  },
  {
    key: "due",
    allLabel: "Any due date",
    noneLabel: "No due date",
    valueOf: (row) => dueBucket(row),
    byCount: true,
  },
  {
    key: "location",
    allLabel: "All locations",
    noneLabel: "No location",
    valueOf: (row) => observationFacts(row).location ?? null,
  },
];

/**
 * Which due bucket a row is in.
 *
 * Buckets rather than dates, because filtering to one exact due date is a
 * question nobody asks and would give a menu one row per day. The design's
 * own `DUE_OF` does the same, off demo data; this one reads the same calendar
 * day displayed in the row and compares it with the reader's local today.
 */
function dueBucket(row: ConsoleRow, now = new Date()): string | null {
  const due = observationFacts(row).due;
  if (!due) return null;
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const days = Math.round((due.getTime() - today.getTime()) / 86_400_000);
  if (days < 0) return "Overdue";
  if (days <= 7) return "Due this week";
  return "Later";
}

// ---------------------------------------------------------------------------
// The log view's days — the phone's daily-log list, on a desk
// ---------------------------------------------------------------------------

/**
 * Daily logs are read BY DAY, and the phone has always shown them that way:
 * a day, the sections inside it, the entries inside those. The console showed
 * one flat list grouped by category across every day at once, which answers a
 * question nobody asks ("everything filed under Deliveries, ever") and cannot
 * answer the one they do ("what happened on Tuesday").
 *
 * So the log view is the phone's arrangement, in the console's chrome: the
 * WALKS list's day heading and card (`AllWalks`), the phone's collapsible
 * category sections inside them, and the phone's own section glyphs — read
 * from `shared/procoreDailyLog`, which is where they now live precisely so
 * these two lists cannot drift apart.
 *
 * Everything here is derived on the client from rows the server already sent.
 * Grouping by day is a presentation of the SAME rows; asking the server for a
 * second grouping would put the console's idea of "which day" (a timezone
 * question, answered by the reader's browser) on the wrong side of the wire.
 */
interface LogSection {
  category: string;
  label: string;
  glyph: string;
  rows: ConsoleRow[];
}

interface LogDay {
  label: string;
  sections: LogSection[];
  count: number;
}

/** Newest day first, and newest entry first within each section. */
export function logDays(rows: ConsoleRow[]): LogDay[] {
  const byDay = new Map<string, Map<string, ConsoleRow[]>>();
  const ordered = [...rows].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );

  for (const row of ordered) {
    const day = dayLabel(row.createdAt);
    // A record with no category still belongs to its day. `notes` is the
    // taxonomy's own catch-all and the category such a row is written under
    // everywhere else, so it is not a bucket invented here.
    const category = row.category || "notes";
    const sections = byDay.get(day) ?? new Map<string, ConsoleRow[]>();
    byDay.set(day, sections);
    sections.set(category, [...(sections.get(category) ?? []), row]);
  }

  return [...byDay.entries()].map(([label, sections]) => ({
    label,
    count: [...sections.values()].reduce((sum, rows) => sum + rows.length, 0),
    sections: [...sections.entries()].map(([category, rows]) => ({
      category,
      label: logCategoryLabel(category),
      glyph: getProcoreDailyLogCategoryGlyph(category),
      rows,
    })),
  }));
}

/** The phone's label for a category, and a readable one for a value the
 *  taxonomy has never heard of (a legacy record, an import). */
function logCategoryLabel(category: string): string {
  const known = PROCORE_DAILY_LOG_CATEGORY_DEFINITIONS[
    category as keyof typeof PROCORE_DAILY_LOG_CATEGORY_DEFINITIONS
  ];
  return known ? known.label : humanize(category);
}

/** `3 ENTRIES · 2 SECTIONS` — the phone's own day meta, word for word. */
function logDayMeta(day: LogDay): string {
  return `${plural(day.count, "entry", "entries")} · ${plural(
    day.sections.length,
    "section",
    "sections",
  )}`.toUpperCase();
}

function LogSectionBlock({
  section,
  first,
  siteNameOf,
  hrefForItem,
  onOpenItem,
}: {
  section: LogSection;
  first: boolean;
  siteNameOf: (jobSiteId: string) => string | undefined;
  hrefForItem: (id: string) => string;
  onOpenItem: (id: string) => void;
}) {
  // Open, like the phone's. A day that arrived collapsed would make the
  // reader click once per section to learn what the day already told them.
  const [open, setOpen] = useState(true);
  return (
    <div
      data-testid="log-section"
      data-category={section.category}
      style={{ borderTop: first ? undefined : `1px solid ${C.line}` }}
    >
      <button
        type="button"
        className="kb"
        aria-expanded={open}
        data-testid="log-section-toggle"
        onClick={() => setOpen((shown) => !shown)}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "11px 15px",
          border: "none",
          background: "transparent",
          cursor: "pointer",
          textAlign: "left",
          font: "inherit",
        }}
      >
        <svg
          width="12"
          height="8"
          viewBox="0 0 12 8"
          fill="none"
          aria-hidden
          style={{
            flexShrink: 0,
            transform: open ? undefined : "rotate(-90deg)",
            transition: "transform 160ms ease",
          }}
        >
          <path
            d="M1 1.5 6 6.5l5-5"
            stroke={C.sub}
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <span
          aria-hidden
          style={{
            fontFamily: FONT_MONO,
            fontSize: 12.65,
            fontWeight: 600,
            width: 14,
            textAlign: "center",
            color: C.ink,
          }}
        >
          {section.glyph}
        </span>
        <span style={{ ...META, flex: 1, minWidth: 0, color: C.ink }}>{section.label}</span>
        <span
          style={{
            fontFamily: FONT_MONO,
            fontSize: 12.65,
            fontWeight: 700,
            color: C.sub,
            background: C.faint,
            minWidth: 22,
            height: 22,
            borderRadius: 11,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "0 6px",
          }}
        >
          {section.rows.length}
        </span>
      </button>
      {open &&
        section.rows.map((row) => (
          <ItemRow
            key={row.id}
            row={row}
            first={false}
            href={hrefForItem(row.id)}
            onOpen={() => onOpenItem(row.id)}
          />
        ))}
    </div>
  );
}

function groupDot(label: string, rows: ConsoleRow[], groupBy: ConsoleGroupBy): string {
  if (groupBy === "project" && rows[0]) return projectDot(rows[0].jobSiteId);
  if (groupBy !== "status") return C.sub2;
  switch (label) {
    case "open":
      return C.gold;
    case "in_progress":
      return C.blue;
    case "ready_for_review":
      return C.amber;
    case "closed":
      return C.green;
    default:
      return C.sub2;
  }
}

// ---------------------------------------------------------------------------
// Inert chips (spec §6.3)
// ---------------------------------------------------------------------------

function Chip({ label, value, locked }: { label: string; value: string; locked?: boolean }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 7,
        height: 32,
        padding: "0 12px",
        borderRadius: 9,
        background: locked ? C.faint : C.surface,
        border: `1px solid ${C.line2}`,
        cursor: "default",
      }}
    >
      <span style={{ ...META, fontSize: 10.35, letterSpacing: "0.7px", color: C.sub2 }}>{label}</span>
      <span style={{ fontSize: 14.37, fontWeight: 600, color: C.ink }}>{value}</span>
      {locked ? (
        <svg width="9" height="9" viewBox="0 0 16 16" fill="none">
          <rect x="3" y="7" width="10" height="7" rx="1.6" stroke={C.sub2} strokeWidth="1.7" />
          <path d="M5.5 7V5a2.5 2.5 0 015 0v2" stroke={C.sub2} strokeWidth="1.7" />
        </svg>
      ) : (
        <svg width="8" height="5" viewBox="0 0 10 6" fill="none">
          <path
            d="M1 1l4 4 4-4"
            stroke={C.sub2}
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The view
// ---------------------------------------------------------------------------

export interface ItemListProps {
  data: ItemsResponse | null;
  failure: ApiError | null;
  loading: boolean;
  view: SavedViewId;
  activeSiteId: string | null;
  /**
   * A column header was tapped.
   *
   * The list does not own its URL — the shell does, and every other
   * parameter on this page is written through it — so the list reports the
   * COLUMN and the shell decides the href; `sortByMenu` below decides what
   * the choice means.
   */
  onColumnSort: (next: ColumnSort | null) => void;
  /**
   * Narrow one column, or clear it with `null`. Like the sort, the shell owns
   * the href so `?workspace=` and every other parameter ride along.
   */
  onColumnFilter: (column: string, value: string | null) => void;
  /** Clear every column filter in ONE navigation — see
   *  `clearColumnFilterParams`. */
  /** The active sort, and the handler that changes it. Sorting happens on
   *  the SERVER (see `projection/priority.ts`), so this only rewrites the
   *  URL and lets the list re-query. */
  sort: ConsoleSort;
  onSort: (next: ConsoleSort) => void;
  /** The live filter state and handler that change it. They write
   *  the URL and let the list re-query — the server decides what the list
   *  contains, so a chip can never disagree with the header's counts. */
  stalledOnly: boolean;
  severities: ConsoleSeverity[] | null;
  onSeverity: (next: ConsoleSeverity | null) => void;
  /**
   * Undo every active filter in ONE navigation.
   *
   * A stale or deep-linked `stalledOnly` parameter can still reach the list
   * from Brief, even though it is no longer a list filter control. Clear it
   * together with Priority, in one href, so the reader never gets stuck in a
   * narrowed list they cannot see a control for.
   */
  onClearFilters: () => void;
  /** `?workspace=personal` on top of the current URL. Rendered as the way
   *  OUT of a workspace the caller may not have — without it a forged or
   *  stale `?workspace=` in a bookmark is a dead end, since the failing
   *  response carries no workspace list to switch from. */
  personalHref: string;
  onGoPersonal: () => void;
  /** A row opens its detail page. Built by the shell so `?workspace=` and
   *  every other console parameter ride along. */
  hrefForItem: (itemId: string) => string;
  onOpenItem: (itemId: string) => void;
  /** A background refresh failed while these rows were already good. They
   *  STAY (`useResource`); the page merely stops claiming to be current. */
  refreshFailure: ApiError | null;
  /** The console URL query, forwarded verbatim to the two writes this list
   *  can make — completing a record, and nudging whoever holds it. */
  search: string;
}

/** What the Priority pill reads. A single priority names itself; anything
 *  else is "Any", because a chip that said "3 severities" would be telling
 *  the reader a number instead of a filter. */
function severityLabel(severities: ConsoleSeverity[] | null): string {
  if (!severities || severities.length !== 1) return "Any";
  const one = severities[0]!;
  return one.charAt(0).toUpperCase() + one.slice(1);
}

export function ItemList({
  data,
  failure,
  loading,
  view,
  activeSiteId,
  sort,
  onSort,
  onColumnSort,
  onColumnFilter,
  stalledOnly,
  severities,
  onSeverity,
  onClearFilters,
  personalHref,
  onGoPersonal,
  hrefForItem,
  onOpenItem,
  refreshFailure,
  search,
}: ItemListProps) {
  /**
   * Ticking a box, and what the list believes while the write is in flight.
   *
   * Four maps rather than one object per row: a row is identified by its id
   * in every one of them, and the alternative — cloning a row object into
   * component state — makes the list carry two copies of a record that a
   * refetch can then disagree with.
   *
   * `doneLocal` is the OVERRIDE, not the truth. The truth is the record's own
   * status; this holds only what has been ticked since the last fetch, so a
   * refresh that brings the record back closed simply agrees with it and a
   * refresh that does not brings the row back open.
   */
  const [doneLocal, setDoneLocal] = useState<Record<string, boolean>>({});
  const [busyIds, setBusyIds] = useState<Record<string, boolean>>({});
  const [writeFailures, setWriteFailures] = useState<Record<string, string>>({});
  const [nudgeItemId, setNudgeItemId] = useState<string | null>(null);

  const rowIsDone = (row: ConsoleRow) => doneLocal[row.id] ?? TERMINAL_ROW_STATUSES.has(row.status);

  const toggleDone = async (row: ConsoleRow) => {
    if (busyIds[row.id]) return;
    const next = !rowIsDone(row);
    setBusyIds((current) => ({ ...current, [row.id]: true }));
    setWriteFailures(({ [row.id]: _gone, ...rest }) => rest);
    setDoneLocal((current) => ({ ...current, [row.id]: next }));

    const result = await postItemEdit(row.kind, row.id, search, {
      // The FIELD's vocabulary, not the console's. `completed` is one of
      // `ACTION_STATUS_VALUES`; the console's own `closed` is a projection of
      // three of them and would be a word the record has never heard.
      status: next ? "completed" : reopenStatusFor(row.status),
    });

    setBusyIds(({ [row.id]: _done, ...rest }) => rest);
    if (!result.ok) {
      // Back where it was, with the reason ON the row. A silent rollback is
      // indistinguishable from a click that missed.
      setDoneLocal((current) => ({ ...current, [row.id]: !next }));
      setWriteFailures((current) => ({
        ...current,
        [row.id]: result.error || "Could not save — try again",
      }));
    }
  };
  /**
   * How much of this list is drawn — see `usePagedList`. The identity is the
   * view, the project and the server's own grouping: change any of those and
   * this is a different list, which starts at the top. A live refresh of the
   * same list is not, and must not throw the reader back to row 40.
   *
   * Derived and called ABOVE the loading and failure returns, because a hook
   * below them runs on the loaded render and not on the loading one, and
   * React counts hooks rather than intentions.
   */
  const provider = data?.sites.find((site) => site.id === activeSiteId)?.syncSource;
  const savedView = projectSavedViews(provider).find((entry) => entry.id === view) ?? SAVED_VIEWS[0]!;

  /**
   * The column the table is ordered by, from the URL.
   *
   * It writes two parameters rather than one so a sorted table is a link,
   * the same as every filter on this page.
   */
  const columnSort = readColumnSort(search) ?? (CREATED_VIEWS.has(view) ? NEWEST_FIRST : null);
  /**
   * The tables' menus name the direction, so picking the one already in force
   * TURNS IT OFF and hands the list back to the order the server sent. Without
   * that there is no way out of a sort except finding the column you used.
   */
  const sortByMenu = (column: string, direction: SortDirection) => {
    const same = columnSort?.column === column && columnSort.direction === direction;
    onColumnSort(same ? null : { column, direction });
  };
  /** Which columns are narrowing the table, from the URL. */
  const columnFilters = readColumnFilters(search, ITEM_COLUMN_FILTERS);
  const filtersOn = anyColumnFilter(columnFilters);
  /**
   * Which age band the flow chart is narrowing the table to.
   *
   * Local, not a URL parameter — see `ItemFlowCards`' header. It is dropped
   * whenever the list underneath it changes to a different population, since
   * a band selected against punch means nothing against observations and a
   * filter nobody can see is a list that is wrong for no visible reason.
   */
  const [ageBand, setAgeBand] = useState<number | null>(null);
  /* ONE clock for the fold and for the band test. Two `new Date()` calls
     either side of a render can straddle midnight, and a row would then be
     counted in one band and filtered by another. */
  const flowClock = useMemo(() => new Date(), [data]);
  useEffect(() => setAgeBand(null), [view, activeSiteId]);
  const groups = (data?.groups ?? [])
    .map((group) => ({
      ...group,
      // Kind selection and project scoping are applied to the SERVER's
      // groups, in place, so group order and row order stay exactly what
      // `listItems` produced. Groups that empty out are dropped rather than
      // rendered blank.
      rows: group.rows.filter(
        (row) =>
          matchesSavedView(savedView, row) &&
          (!activeSiteId || row.jobSiteId === activeSiteId),
      ),
    }))
    .filter((group) => group.rows.length > 0);
  /* The chart is folded from every open row, BEFORE the band narrows
     anything — a bar whose own numbers moved when you tapped it could not be
     used to decide where to look next. The table below gets the narrowed
     set; the chart above keeps the whole picture. */
  const flowRows = groups.flatMap((group) => group.rows);
  const banded =
    ageBand === null
      ? groups
      : groups
          .map((group) => ({
            ...group,
            rows: group.rows.filter((row) => inAgeBand(row, ageBand, flowClock)),
          }))
          .filter((group) => group.rows.length > 0);
  /* The column menus narrow the TABLE and not the chart above it, exactly as
     the age band does: a chart that renumbered every time a column was
     filtered could not be used to decide which column to filter next. The
     header chips and the clear-all control are what say the list is
     narrowed. */
  const filtered = filtersOn
    ? banded
        .map((group) => ({
          ...group,
          rows: applyColumnFilters(group.rows, ITEM_COLUMN_FILTERS, columnFilters),
        }))
        .filter((group) => group.rows.length > 0)
    : banded;
  const rows = filtered.flatMap((group) => group.rows);
  /**
   * What each column's menu offers.
   *
   * Built from the rows the SAVED VIEW and the project scope admit, before
   * the column filters narrow anything — `columnFilterOptions` then excludes
   * each column's own filter from its own pool, so a menu never collapses to
   * the one value already chosen and every count is honest about what
   * picking it would leave.
   */
  const columnOptions: Record<string, ColumnFilterOption[]> = Object.fromEntries(
    ITEM_COLUMN_FILTERS.map((def) => [
      def.key,
      columnFilterOptions(flowRows, ITEM_COLUMN_FILTERS, columnFilters, def, (row) =>
        Math.max(0, Math.floor((flowClock.getTime() - Date.parse(row.createdAt)) / 86_400_000)),
      ),
    ]),
  );
  /* Folded on every render rather than memoized: `rows` is a fresh array each
     time, so a `useMemo` keyed on it would recompute anyway while pinning a
     stale clock into the age bands. The fold is one pass over a few hundred
     rows. */
  const flowNoun = FLOW_NOUN[savedView.id];
  const flow = flowNoun ? buildItemFlow(flowRows, flowClock) : null;
  /* Punch, Observations and Autodesk Issues are the records table — below,
     and in the skeleton that holds its shape while it loads. One predicate,
     so the loading screen cannot draw a different page from the loaded one. */
  const recordsView = view === "punch" || view === "obs" || view === "issues";
  const paged = usePagedList(
    `${view}|${activeSiteId ?? ""}|${data?.groupBy ?? ""}`,
    rows.length,
  );

  if (failure) {
    return (
      <FailurePanel failure={failure} personalHref={personalHref} onGoPersonal={onGoPersonal} />
    );
  }

  if (!data) {
    if (loading) return <ViewSkeleton shape={recordsView ? "records" : flowNoun && view === "task" ? "items-flow" : "items"} />;
    return (
      <Centered>
        <div style={{ ...META, fontSize: 10.93, color: C.sub2 }}>
          {loading ? "Loading" : "No data"}
        </div>
      </Centered>
    );
  }

  const site = activeSiteId ? data.sites.find((entry) => entry.id === activeSiteId) : undefined;

  /* Punch, Observations and Autodesk Issues are the records table
     ("Led AI - Records Table"): its own top bar, filters and three layouts.
     It reads the rows this view and project admit — the server's own
     `?severity=` is already applied — and narrows them itself. */
  if (recordsView) {
    return (
      <RecordsWorkspace
        vocab={view}
        rows={groups.flatMap((group) => group.rows)}
        sites={data.sites}
        activeSiteId={activeSiteId}
        search={search}
        me={{ userId: data.userId, name: data.caller?.name }}
        severities={severities}
        onSeverity={onSeverity}
        onColumnSort={onColumnSort}
        onColumnFilter={onColumnFilter}
        onClearFilters={onClearFilters}
        hrefForItem={hrefForItem}
        onOpenItem={onOpenItem}
        refreshFailure={refreshFailure}
        emptyBody={
          data.availableWorkspaces.length > 1
            ? "This workspace has none in this view. Try another workspace from the switcher, or create one."
            : "They appear here as soon as a walk is reviewed — or create one now."
        }
      />
    );
  }

  const openCount = rows.filter(
    (row) => row.status !== "closed" && row.status !== "logged",
  ).length;

  const crumb = site
    ? `Project · ${savedView.name}`
    : savedView.id === "all"
      ? "All projects · All items"
      : `View · ${savedView.name}`;
  const title = site ? site.name || site.id : savedView.name;
  const siteNameOf = (jobSiteId: string) =>
    data.sites.find((entry) => entry.id === jobSiteId)?.name || jobSiteId;

  return (
    <>
      {/*
        ---------- header ----------

        Capped and centred on the SAME column as the table below it. It used to
        be full-width while the table was capped at `TABLE_MAX_WIDTH`, so on a
        wide pane the title and the filters sat left of the rows they belonged
        to and drifted further apart the wider the window got.
      */}
      <div style={{ flexShrink: 0, padding: "22px 28px 0" }}>
        <div style={{ width: "100%", maxWidth: TABLE_MAX_WIDTH, margin: "0 auto" }}>
        <div
          style={{
            display: "flex",
            // The design hangs the title block and the action off the same
            // BASELINE-ish edge, so a two-line title grows upward and the
            // button beside it stays put.
            alignItems: "flex-end",
            gap: 16,
            flexWrap: "wrap",
          }}
        >
          <div>
            {/* Title, then the count as its own line beneath — the design's
                `issuesTitle` over `issuesSub`. The crumb used to lead and the
                count hid inside it, which put the least useful string on the
                page in the most prominent place and made "how many items am I
                looking at" something you read sideways out of a breadcrumb. */}
            <div
              style={{
                fontSize: 24,
                fontWeight: 700,
                letterSpacing: "-0.48px",
                margin: "0 0 4px",
              }}
            >
              {title}
            </div>
            <div style={{ fontSize: 16.1, color: C.tileMeta }} data-testid="items-count">
              {`${crumb} · ${rows.length} ${rows.length === 1 ? "item" : "items"} · ${openCount} open`}
            </div>
          </div>
          <span style={{ flex: 1 }} />
        </div>

        {/* Filter cards share URL state with the table column menus. */}
        {/* The design's filter row: cards that share the width, no rule under
            them. The bottom hairline went with the chips — the table below
            draws its own edge, and two lines a few pixels apart read as a
            mistake. */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            flexWrap: "wrap",
            marginTop: 22,
            paddingBottom: 18,
          }}
          data-testid="filter-chips"
        >
          {/* No KIND filter. Which kind you are looking at is what the
              sidebar's own rows are for — Observations, Tasks, Punch, Photos
              — and a filter that duplicated them put the same navigation in
              two places, one of which did not look like navigation
              (user, 2026-09-10). */}
          <MenuControl
            variant="filter"
            testId="severity"
            label="Priority"
            active={(severities?.length ?? 0) > 0}
            valueIcon={severities?.length === 1 ? <PriorityMark level={PRIORITY_RANK[severities[0]!] ?? 0} /> : undefined}
            value={severityLabel(severities)}
            options={[
              { value: "any", label: "Any", hint: "Every priority, rated or not", divider: true },
              { value: "high", label: "High", hint: "High priority only", icon: <PriorityMark level={3} /> },
              { value: "medium", label: "Medium", hint: "Medium priority only", icon: <PriorityMark level={2} /> },
              { value: "low", label: "Low", hint: "Low priority only", icon: <PriorityMark level={1} /> },
            ]}
            onSelect={(next) => onSeverity(next === "any" ? null : (next as ConsoleSeverity))}
          />
          {/* ASSIGNEE — the design's card, on both its punch and its
              observations row (15a, 14a).

              It was already a filter; it was just only reachable from the
              Assignee COLUMN's menu, which is a place nobody looked. Both
              surfaces write the same `f.assignee` parameter, so this is one
              filter shown twice and not two that can disagree — the column
              chip and this card always read the same value.

              The design's fifth punch card, `Ball in court`, is NOT here:
              `ConsoleItem.ballHolder` is a denormalized copy of the record's
              assignee (see `prepareNudge`), so in this console's data the two
              are the same field. Procore distinguishes them; we would be
              drawing two cards off one column. `Notified` is missing for the
              blunter reason that no record here carries it. */}
          <MenuControl
            variant="filter"
            testId="assignee"
            label="Assignee"
            active={!!columnFilters["assignee"]}
            value={columnFilterLabel(columnOptions["assignee"] ?? [], columnFilters.assignee === NO_VALUE ? "Unassigned" : columnFilters.assignee ?? "Anyone")}
            valueIcon={columnFilters.assignee && columnFilters.assignee !== NO_VALUE ? <AssigneeAvatar name={columnFilters.assignee} /> : undefined}
            options={(columnOptions["assignee"] ?? []).map((option, index) => ({
              value: option.value ?? ALL_OPTION,
              label: option.label,
              icon: option.value && option.value !== NO_VALUE ? <AssigneeAvatar name={option.label} size={30} /> : undefined,
              detail: option.value && option.value !== NO_VALUE
                ? [...new Set(flowRows.filter(row => observationFacts(row).assignee === option.value && row.assigneeCompanySource !== "assignee-name").map(row => row.assigneeCompany).filter(Boolean))].join(" · ") || undefined
                : undefined,
              meta: String(option.count),
              divider: index === 0,
            }))}
            onSelect={(next) => onColumnFilter("assignee", next === ALL_OPTION ? null : next)}
          />
          {(["location", "company"] as const).map((key) => {
            const definition = ITEM_COLUMN_FILTERS.find(def => def.key === key)!;
            return <MenuControl
              key={key}
              variant="filter"
              testId={key}
              label={key === "location" ? "Location" : "Company"}
              active={!!columnFilters[key]}
              value={columnFilterLabel(columnOptions[key] ?? [], columnFilters[key] === NO_VALUE ? definition.noneLabel : columnFilters[key] ?? definition.allLabel)}
              options={(columnOptions[key] ?? []).map((option, index) => ({
                value: option.value ?? ALL_OPTION,
                label: option.label,
                meta: String(option.count),
                divider: index === 0,
              }))}
              onSelect={(next) => onColumnFilter(key, next === ALL_OPTION ? null : next)}
            />;
          })}
          {/* The way BACK. Priority, Assignee, the age band, and a
              stalled-only deep link from Brief can all narrow this list.
              One gesture undoes every one, so a reader never has to edit a
              URL to recover.
              Shown only when there is something to clear.

              It does NOT reset the kind any more. With the Kind filter gone,
              the kind IS the page you are on — clearing it would throw a
              reader off Observations and onto All items, which is navigation,
              not clearing a filter. */}
          {(stalledOnly || (severities?.length ?? 0) > 0 || filtersOn || ageBand !== null) && (
            <button
              type="button"
              className="kb"
              data-testid="clear-filters"
              onClick={() => {
                // The column filters and the band are filters too, and this
                // is the row's only way back. It used to clear two of the
                // five things that could be narrowing the list, which is a
                // "Clear all" that leaves the list narrowed.
                onClearFilters();
                setAgeBand(null);
              }}
              style={{
                border: "none",
                background: "transparent",
                padding: "0 4px",
                font: "inherit",
                fontSize: 14.95,
                fontWeight: 500,
                color: C.tileMeta,
                whiteSpace: "nowrap",
                cursor: "pointer",
              }}
            >
              Clear all
            </button>
          )}
          {/* No GROUP control. It cycled a URL parameter through five values
              with no way to see what the next one would be, and the tables
              below do their own grouping in their own vocabulary
              (user, 2026-09-10). The server still groups by `?groupBy=`; what
              is gone is the pill.

              And no SORT control at all. It offered a fixed menu of the
              server's own orderings — stalled, attention, severity — which
              is the wrong shape twice over on a table: the reader is looking
              at a column and wants THAT column ordered, and the menu could
              not offer most of them (no "by assignee", no "by due date").
              Every view that reaches this list now draws a header row and
              sorts by tapping it (user, 2026-09-11).

              I first kept the pill for `view === "log"`, on the reasoning
              that a day-grouped list has no header to tap. That view never
              reaches this component — the shell routes `log` to `DailyLog`
              — so the gate was dead the moment it was written, and a control
              nobody can see is worse than one that is gone. */}
        </div>
        </div>
      </div>

      {/* ---------- rows ---------- */}
      {/*
        The bottom padding is the Ask composer's CLEARANCE, not a design
        margin. This list rolls its own scroller instead of using `ViewBody`,
        and it was left at 34px while the composer that floats over it is
        148px tall — so scrolling to the end of a long list (a full punch
        list, say) parked the last row underneath the composer where it could
        not be read. Every other scrolling body in the console already ends in
        this constant for exactly this reason; this one was simply missed.
      */}
      <div
        className="sb"
        style={{
          flex: 1,
          minHeight: 0,
          overflow: "auto",
          padding: `6px 28px ${ASK_COMPOSER_CLEARANCE}px`, paddingBottom: `var(--ask-composer-clearance, ${ASK_COMPOSER_CLEARANCE}px)`,
        }}
      >
        {/* One column, capped and centred — see `TABLE_MAX_WIDTH`. The
            scroller stays full-width so the scrollbar rides the pane edge
            where every other list in the console puts it. */}
        <div style={{ width: "100%", maxWidth: TABLE_MAX_WIDTH, margin: "0 auto" }}>
        {refreshFailure && <StaleBanner failure={refreshFailure} />}
        {/* Above the table and inside its cap, so the two share an edge. Not
            drawn on an empty view: the cards would be the only thing on the
            page, describing nothing. */}
        {flow && flowNoun && groups.length > 0 && (
          <ItemFlowCards
            flow={flow}
            noun={flowNoun}
            maxWidth={TABLE_MAX_WIDTH}
            band={ageBand}
            onBand={setAgeBand}
          />
        )}
        {/* The band the table is narrowed to, and the way out of it — see
            `ItemFlowBandChip`. Below the cards, as the design places it. */}
        {((flow && ageBand !== null) || filtersOn) && (
          <div
            style={{
              width: "100%",
              maxWidth: TABLE_MAX_WIDTH,
              margin: "0 auto 14px",
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            {flow && ageBand !== null && (
              <ItemFlowBandChip flow={flow} band={ageBand} onClear={() => setAgeBand(null)} />
            )}
            {/* No second "Clear filters" here. There is one way back and it
                is `Clear all` in the filter row, which now covers the column
                filters too — two clear controls in one view, with different
                scopes and different words, was a reader having to work out
                which of them undoes the thing in front of them. */}
          </div>
        )}
        {filtered.length === 0 && (filtersOn || ageBand !== null) ? (
          /* Narrowed to nothing. Not "no punch items yet" — that is a claim
             about the workspace, and the reason the table is empty is sitting
             in the header chips right above it. */
          <EmptyPanel
            title="Nothing matches these filters"
            body="Clear all in the filter row above widens it again."
          />
        ) : filtered.length === 0 ? (
          <EmptyPanel
            // Name what is empty. "Nothing in this team yet" over a team
            // holding 196 items — of which this VIEW happens to match none —
            // tells the reader something false about their workspace. The
            // saved view's own name is the accurate subject.
            title={
              savedView.id !== "all"
                ? `No ${savedView.name.toLowerCase()} yet`
                : `Nothing in ${
                    data.activeWorkspace.type === "personal" ? "Personal" : "this team"
                  } yet`
            }
            body={
              data.availableWorkspaces.length > 1
                ? "This workspace has no items matching this view. Try another workspace from the switcher."
                : "Items appear here as soon as the first walk is reviewed."
            }
          />
        ) : view === "log" ? (
          pagedDays(logDays(rows), paged.shown).map((day) => (
            <div key={day.label} data-testid="log-day" data-day={day.label}>
              <div
                style={{
                  display: "flex",
                  alignItems: "baseline",
                  gap: 10,
                  margin: "24px 0 10px 2px",
                }}
              >
                <span style={{ fontSize: 17.25, fontWeight: 800, letterSpacing: "-0.3px" }}>
                  {day.label}
                </span>
                <span style={{ ...META, fontSize: 10.35, color: C.sub2 }} data-testid="log-day-meta">
                  {logDayMeta(day)}
                </span>
              </div>
              <div
                style={{
                  background: C.surface,
                  border: `1px solid ${C.hair}`,
                  borderRadius: 14,
                  overflow: "hidden",
                  boxShadow: `0 1px 2px ${C.softShadow}`,
                }}
              >
                <ItemColumns
                  integration={syncColumnName(rows)}
                  sort={columnSort}
                  onSort={sortByMenu}
                />
                {day.sections.map((section, index) => (
                  <LogSectionBlock
                    key={section.category}
                    section={section}
                    first={index === 0}
                    siteNameOf={siteNameOf}
                    hrefForItem={hrefForItem}
                    onOpenItem={onOpenItem}
                  />
                ))}
              </div>
            </div>
          ))
        ) : (
          pagedGroups(
            groups.map((group) => ({ ...group, total: group.rows.length })),
            paged.shown,
          ).map((group) => (
            <div key={group.label} style={{ marginBottom: 22 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 9, padding: "10px 2px 8px" }}>
                <span
                  style={{
                    width: 7,
                    height: 7,
                    borderRadius: 4,
                    background: groupDot(group.label, group.rows, data.groupBy),
                  }}
                />
                <span style={{ ...META, color: C.ink }}>{humanize(group.label)}</span>
                <span style={{ fontFamily: FONT_MONO, fontSize: 11.5, color: C.sub2 }}>
                  {group.total} {group.total === 1 ? "item" : "items"}
                </span>
              </div>
              <div
                style={{
                  background: C.surface,
                  border: `1px solid ${C.hair}`,
                  borderRadius: 14,
                  overflow: "hidden",
                  boxShadow: `0 1px 2px ${C.softShadow}`,
                }}
              >
                <ItemColumns
                  integration={syncColumnName(rows)}
                  sort={columnSort}
                  onSort={sortByMenu}
                  created={view === "task"}
                />
                {/* Tasks order within each group by the column in force —
                    newest capture first unless the URL names another
                    (LED-772). The other views keep the server's order. */}
                {(view === "task"
                  ? sortByColumn(group.rows, columnSort, itemColumnCompare(columnSort?.direction ?? "asc", siteNameOf))
                  : group.rows
                ).map((row, index) => (
                  <ItemRow
                    key={row.id}
                    created={view === "task"}
                    row={row}
                    first={index === 0}
                    href={hrefForItem(row.id)}
                    onOpen={() => onOpenItem(row.id)}
                    done={rowIsDone(row)}
                    busy={busyIds[row.id] ?? false}
                    failure={writeFailures[row.id]}
                    onToggle={canComplete(row) ? () => void toggleDone(row) : undefined}
                    onNudge={() => setNudgeItemId(row.id)}
                    siteName={activeSiteId ? undefined : siteNameOf(row.jobSiteId)}
                  />
                ))}
              </div>
            </div>
          ))
        )}
        {/* The status tables draw their own end line, in their own noun —
            this one under them said "40 of 61 items" beneath "40 of 61
            observations". */}
        {(
          <PagedListEnd
            shown={paged.shown}
            total={rows.length}
            noun="item"
            nouns="items"
            sentinel={paged.sentinel}
          />
        )}
        </div>
      </div>

      {/* The nudge sheet, opened from a row's own action. It is `position:
          absolute; inset: 0` and its parent must be positioned — the pane
          this list sits in is, so it covers the list rather than the whole
          console. */}
      {nudgeItemId && (
        <NudgeSheet
          itemId={nudgeItemId}
          search={search}
          onClose={() => setNudgeItemId(null)}
        />
      )}
    </>
  );
}

/**
 * The first `budget` ROWS of a grouped list, with the groups they fall in.
 *
 * Paging a grouped list by group would draw a wildly different number of rows
 * each time (one status holds three items, another holds ninety). The budget
 * is rows, and a group is cut where the budget runs out — the same rule for
 * both arrangements, so a reader scrolling either one gets the same amount of
 * page per scroll.
 */
export function pagedGroups<T extends { rows: ConsoleRow[] }>(groups: T[], budget: number): T[] {
  const out: T[] = [];
  let left = budget;
  for (const group of groups) {
    if (left <= 0) break;
    out.push(group.rows.length <= left ? group : { ...group, rows: group.rows.slice(0, left) });
    left -= group.rows.length;
  }
  return out;
}

/** The same cut, one level deeper: days hold sections hold rows. */
export function pagedDays(days: LogDay[], budget: number): LogDay[] {
  const out: LogDay[] = [];
  let left = budget;
  for (const day of days) {
    if (left <= 0) break;
    const sections: LogSection[] = [];
    for (const section of day.sections) {
      if (left <= 0) break;
      sections.push(
        section.rows.length <= left ? section : { ...section, rows: section.rows.slice(0, left) },
      );
      left -= section.rows.length;
    }
    // The day's own meta still counts the whole day: "3 entries · 2 sections"
    // is a fact about the day, not about how much of it is drawn.
    out.push({ ...day, sections });
  }
  return out;
}
